import React, { forwardRef, useMemo } from 'react';
import { Box, IconButton, styled, Typography } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { GearIcon } from 'assets';
import { ControlTooltip } from 'components';

import styles from './ToggleSettingsButton.module.scss';

type ToggleSettingsButtonProps = {
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  iconColor?: string;
  withTooltip?: boolean;
  onClick: () => void;
  withLabel?: boolean;
};

const GearIconStyled = styled(GearIcon)(() => ({
  color: '#fff',
}));

export const ToggleSettingsButton = forwardRef<HTMLButtonElement, ToggleSettingsButtonProps>(
  (
    { disabled, className, iconClassName, iconColor, withTooltip, onClick, withLabel }: ToggleSettingsButtonProps,
    ref,
  ) => {
    const { t, i18n } = useTranslation('providerRoom');
    const isRtl = i18n.dir() === 'rtl';
    const tooltipMessage = useMemo(() => {
      let tooltipMessage = '';

      if (withTooltip) {
        tooltipMessage = t('menus.settings');
      }

      return tooltipMessage;
    }, [withTooltip, t]);

    const sx = useMemo(
      () => ({
        backgroundColor: withLabel ? 'transparent !important' : 'rgb(0 0 0 / 50%) !important',
      }),
      [withLabel],
    );

    return (
      <ControlTooltip title={tooltipMessage} isDisabled={disabled}>
        <Box component='span' display='block' width={withLabel ? '100%' : undefined} className={classnames(className)}>
          <IconButton
            disabled={disabled}
            className={classnames(
              { [styles.deviceButton]: !withLabel, [styles.deviceButtonWithLabel]: withLabel },
              className,
            )}
            sx={{
              ...sx,
              ...(withLabel && {
                justifyContent: 'flex-start',
                width: '100%',
              }),
            }}
            onClick={onClick}
            aria-label={t('aria.settings')}
            ref={ref}
          >
            <GearIconStyled
              className={classnames(styles.icon, iconClassName)}
              sx={withLabel ? { color: iconColor || 'var(--app-text-muted)' } : {}}
            />
            {withLabel && (
              <Typography
                component='p'
                variant='body2'
                sx={{
                  color: 'var(--app-text-default)',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'keep-all',
                  overflowWrap: 'normal',
                  textAlign: isRtl ? 'right' : 'left',
                  paddingInlineStart: '10px',
                }}
              >
                {t('menus.settings')}
              </Typography>
            )}
          </IconButton>
        </Box>
      </ControlTooltip>
    );
  },
);
